import type { LineSeriesOption } from 'echarts'

import type { PeriodWindow } from '@/components/charts/history/timeAxis'
import { formatDate } from '@/utils/formatDate'

import type { AlertMarker } from './types'

/** Series name for the raised-time marker. Kept out of legends — it carries no data. */
export const ALERT_MARKER_SERIES = '__time_markers__'

/** Grid top (px) a chart needs when a marker is drawn: its label sits above the plot. */
export const ALERT_MARKER_TOP_PX = 64

/**
 * Vertical line plus severity-icon label at the moment an alert was raised, used by the
 * alert acknowledgement page to show where in the window the alert fired.
 *
 * Returns undefined when there is no marker, or when the raised time falls outside the
 * drawn window — ECharts would otherwise clamp the line to the axis edge, which reads as
 * "the alert fired here" at a time it did not.
 */
export function buildAlertMarkerSeries(
  alertMarkers: AlertMarker | undefined,
  periodWindow: PeriodWindow,
  zone: string
): LineSeriesOption | undefined {
  if (!alertMarkers?.raisedTime) return undefined

  const raisedMs = new Date(alertMarkers.raisedTime).getTime()
  if (!Number.isFinite(raisedMs) || raisedMs < periodWindow[0] || raisedMs > periodWindow[1]) {
    return undefined
  }

  const severityColor = alertMarkers.severityCode === 1 ? 'green' : alertMarkers.severityCode === 2 ? 'yellow' : 'red'
  const iconUrl = `/images/alert-${severityColor}-triangle.svg`

  return {
    type: 'line',
    name: ALERT_MARKER_SERIES,
    xAxisIndex: 0,
    yAxisIndex: 0,
    data: [],
    silent: true,
    symbol: 'none',
    lineStyle: { width: 0 },
    markLine: {
      silent: true,
      symbol: ['none', 'none'],
      symbolSize: [0, 0],
      data: [
        {
          xAxis: raisedMs,
          lineStyle: { color: '#ef4444', type: 'solid', width: 2 },
          label: {
            show: true,
            formatter:
              `{icon|} {time|${formatDate(alertMarkers.raisedTime, 'dd-MMM-yyyy hh:mm a ZZZZ', zone)}}` +
              (alertMarkers.mpName ? `\n{mp|${alertMarkers.mpName}}` : ''),
            rich: {
              icon: {
                backgroundColor: { image: iconUrl },
                width: 14,
                height: 14,
                align: 'center' as const,
              },
              time: {
                fontSize: 12,
                color: '#000',
                lineHeight: 18,
              },
              mp: {
                fontSize: 11,
                color: '#6b7280',
                lineHeight: 16,
              },
            },
            position: 'end',
            distance: 4,
            fontSize: 12,
            color: '#000',
            backgroundColor: '#fff',
            borderColor: '#d1d5db',
            borderWidth: 1,
            borderRadius: 4,
            padding: [3, 6],
            shadowBlur: 6,
            shadowColor: 'rgba(0,0,0,0.15)',
            shadowOffsetX: 1,
            shadowOffsetY: 2,
          },
        },
      ],
    },
  }
}
